import { ChevronDown, Maximize2, Minimize2, X } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { IconBtn } from "../../ui";
import type { WindowState } from "./composerWindow";
import type { SaveState } from "./drafts";

/** "14:03" in the browser's own time - when the draft was last saved. */
const clock = (d: Date) => `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;

/**
 * The composer's title bar: the subject as the window's name, how the draft is
 * doing, and minimise, maximise and close. Its own file to keep `Composer.tsx`
 * about the letter rather than about the window around it.
 *
 * The save note uses the page builder's words ("Saving...", "Saved 14:03"), so
 * "is this saved?" has one answer everywhere in the product. It stays quiet
 * until there is a draft to talk about.
 */
export function ComposerTitleBar({
  title,
  state,
  setState,
  phone,
  close,
  save,
}: {
  title: string;
  state: WindowState;
  setState: (next: WindowState) => void;
  /** Full screen already: there is no size to toggle. */
  phone: boolean;
  close: () => void;
  save?: { state: SaveState; savedAt: Date | null };
}) {
  const t = useT();
  const note =
    save?.state === "saving" ? t("drafts.saving")
      : save?.state === "error" ? t("drafts.saveFailed")
        : save?.state === "saved" ? (save.savedAt ? t("drafts.savedAt", { time: clock(save.savedAt) }) : t("drafts.saved"))
          : null;
  return (
    <>
    {/* Collapsed, the title bar IS the window, so it is also the control
        that brings it back. Double click takes it straight to full size. */}
    <div
      className={cn(
        "flex flex-none select-none items-center gap-2 py-[15px] pl-[22px] pr-3.5",
        state !== "min" && "border-b border-[var(--crm-hair)]",
      )}
      onClick={() => state === "min" && setState("dock")}
      onDoubleClick={() => !phone && setState(state === "max" ? "dock" : "max")}
    >
      <span className="crm-trunc flex-1 text-[13.5px] font-bold tracking-[-0.025em]">{title}</span>
      {note ? (
        <span
          className={cn("flex-none text-[11px] font-[550] tabular-nums", save?.state === "error" ? "text-destructive" : "text-muted-foreground")}
          role="status"
          aria-live="polite"
        >
          {note}
        </span>
      ) : null}
      <IconBtn
        className="size-[30px] border-0"
        onClick={(e) => {
          e.stopPropagation();
          setState(state === "min" ? "dock" : "min");
        }}
        title={t("compose.minimize")}
        aria-label={t("compose.minimize")}
      >
        <ChevronDown className={cn("size-4 transition-transform", state === "min" && "rotate-180")} />
      </IconBtn>
      {phone ? null : (
        <IconBtn
          className="size-[30px] border-0"
          onClick={(e) => {
            e.stopPropagation();
            setState(state === "max" ? "dock" : "max");
          }}
          title={t(state === "max" ? "compose.restore" : "compose.maximize")}
          aria-label={t(state === "max" ? "compose.restore" : "compose.maximize")}
        >
          {state === "max" ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
        </IconBtn>
      )}
      <IconBtn
        className="size-[30px] border-0"
        onClick={(e) => {
          e.stopPropagation();
          close();
        }}
        title={t("compose.close")}
        aria-label={t("compose.close")}
      >
        <X className="size-4" />
      </IconBtn>
    </div>
    </>
  );
}
